import React from "react";
import { useState, useCallback } from "react";
import {
  Grid,
  Box,
  Card,
  useTheme,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useApi } from "@platform/api";
import { Button, TextField, Typography } from "@platform/design-system";

import { useToastNotificationService, useValidateRules } from "../../hooks";
import { STATE_RULES, initPasswordRulesConfirm } from "../../constants";
import { Loader, RenderRules } from "../../components";

const PASSWORD = "new_password";
const PASSWORD_CONFIRM = "new_password_confirm";

const initState = {
  data: {
    [PASSWORD]: "",
    [PASSWORD_CONFIRM]: "",
  },
  error: null,
  showPassword: {
    [PASSWORD]: false,
    [PASSWORD_CONFIRM]: false,
  },
};

function UpdatePassword({ handleGoBack }) {
  const theme = useTheme();
  const [rulesConfirm, setRulesConfirm] = useState([]);
  const [validateRule, rules, restoreRules] = useValidateRules();
  const axiosInstance = useApi();
  const { createToastNotification } = useToastNotificationService();

  const [state, setState] = useState(initState);

  const validateRuleConfirm = (val, current = state.data[PASSWORD]) => {
    const newRules = [...initPasswordRulesConfirm];

    if (!!current && current === val)
      newRules[0] = { ...newRules[0], state: STATE_RULES.DONE };
    else newRules[0] = { ...newRules[0], state: STATE_RULES.WRONG };

    setRulesConfirm(newRules);
  };

  const updateState = useCallback(function updateState(newState) {
    setState((s) => ({ ...s, ...newState }));
  }, []);

  function handleChange({ target }) {
    const { name, value } = target;
    if (name === PASSWORD) {
      validateRule(value);
      validateRuleConfirm(state.data[PASSWORD_CONFIRM], value);
    }
    if (name === PASSWORD_CONFIRM) validateRuleConfirm(value);
    updateState({ data: { ...state.data, [name]: value } });
  }

  function validateForm() {
    return (
      !!state.data[PASSWORD] &&
      state.data[PASSWORD] === state.data[PASSWORD_CONFIRM] &&
      rules.every((rule) => rule.state === STATE_RULES.DONE)
    );
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }
    try {
      updateState({ loading: true });
      await axiosInstance.post("/brokerportal/v1/changePassword", {
        newPassword: state.data[PASSWORD],
      });
      createToastNotification({
        severity: "success",
        message: "Password has been updated!",
      });
      handleGoBack();
    } catch {
      createToastNotification({
        severity: "error",
        message: "Password couldn't be updated. Please retry later",
      });
    } finally {
      updateState({ loading: false });
    }
  }

  const handleShowPassword = (key) => {
    updateState({
      showPassword: { ...state.showPassword, [key]: !state.showPassword[key] },
    });
  };

  if (state.state === "loading") {
    return <Loader />;
  }

  const handleGoBackClick = () => {
    setState(initState);
    setRulesConfirm([]);
    restoreRules();
    handleGoBack();
  };

  return (
    <Box maxWidth={theme.breakpoints.values.sm}>
      <Typography variant="display-medium" marginBottom={5}>
        Profile
      </Typography>
      <Card>
        <Box
          component="form"
          display="flex"
          flexDirection="column"
          alignItems="center"
          onSubmit={handleSubmit}
          px={3}
        >
          <Box width="100%" p={3}>
            <Typography variant="header-large">Update Password</Typography>
            <Box mt={3} display="flex" flexDirection="column" gap={3}>
              <Grid container spacing={1}>
                <Grid size={12}>
                  <TextField
                    label="New password"
                    required
                    fullWidth
                    type={state.showPassword[PASSWORD] ? "text" : "password"}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={state.state === "success"}
                    value={state.data[PASSWORD]}
                    onChange={handleChange}
                    name={PASSWORD}
                    autoComplete="new-password"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => handleShowPassword(PASSWORD)}
                              edge="end"
                            >
                              {state.showPassword[PASSWORD] ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <RenderRules rules={rules} />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="Confirm Password"
                    required
                    fullWidth
                    type={
                      state.showPassword[PASSWORD_CONFIRM] ? "text" : "password"
                    }
                    value={state.data[PASSWORD_CONFIRM]}
                    onChange={handleChange}
                    name={PASSWORD_CONFIRM}
                    autoComplete="new-password"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle confirm password visibility"
                              onClick={() =>
                                handleShowPassword(PASSWORD_CONFIRM)
                              }
                              edge="end"
                            >
                              {state.showPassword[PASSWORD_CONFIRM] ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <RenderRules rules={rulesConfirm} />
                </Grid>
              </Grid>
            </Box>
            <Box mt={3}>
              <Button
                type="submit"
                variant="primary"
                disabled={!validateForm()}
              >
                Confirm
              </Button>
              <Button variant="tertiary" onClick={handleGoBackClick}>
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

export default UpdatePassword;
